/**
 * canvas-engine/renderer/elements/HtmlElement.jsx
 *
 * OWN per-type element renderer for `html` (custom HTML/CSS) elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 * It imports only its own canvas-engine css siblings.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the sanitized inner HTML,
 * filling its positioned box. The parent CanvasRenderer owns positioning. DOM
 * output is identical in builder and reader modes — both run the SAME pure
 * sanitizer/scoper. (Req 4.3, 26.1, 26.2, 26.3)
 *
 * CSS scoping: a stable scope id is derived from the element id; the author CSS
 * is run through the OWN `scopeCustomCss` (which sanitizes via `sanitizeCss` and
 * prefixes every selector with `#${scopeId}`). The scoped CSS is rendered in a
 * <style> tag and the sanitized HTML is rendered inside a wrapper carrying that
 * id with `isolation: isolate`. Only the SANITIZED html ever reaches
 * dangerouslySetInnerHTML.
 *
 * NOTE on interop: `sanitizeHtml` and `scopeCustomCss` are CommonJS modules
 * (`module.exports = { ... }`). Under the CRA/Babel ESM interop these named
 * imports resolve to the exported members, which is the form used elsewhere in
 * this engine (e.g. scopeCustomCss requires sanitizeCss).
 */

import React from 'react';
import { sanitizeHtml } from '../../css/sanitizeHtml';
import { scopeCustomCss } from '../../css/scopeCustomCss';

/** Build a CSS-id-safe scope token from an element id. */
function scopeIdFor(element) {
  const rawId = element && element.id != null ? String(element.id) : '';
  const safe = rawId.replace(/[^a-zA-Z0-9_-]/g, '-');
  return `ce-html-${safe || 'el'}`;
}

/**
 * Render sanitized custom HTML with section-scoped custom CSS.
 *
 * @param {object} props
 * @param {object} props.element normalized `html` Canvas_Element
 */
export default function HtmlElement({ element }) {
  const content = (element && element.content) || {};

  const scopeId = scopeIdFor(element);
  const safeHtml = sanitizeHtml(typeof content.html === 'string' ? content.html : '');
  const scopedCss = scopeCustomCss(typeof content.css === 'string' ? content.css : '', scopeId);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    isolation: 'isolate',
    overflow: 'auto',
  };

  return (
    <div className="ce-html" id={scopeId} style={rootStyle}>
      {scopedCss ? <style>{scopedCss}</style> : null}
      {/* Only the SANITIZED html is injected. (Req 26.x) */}
      <div className="ce-html__content" dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </div>
  );
}
